import { type ReactNode, useEffect, useRef, useState } from "react";
import { IconButton, styled, type BoxProps, Box } from "~/common/mui";
import { useIsMinDesktopSize } from "../../hooks/useMediaQuery";
import { withDefaults } from "~/common/hoc/withDefaults";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

export interface CarouselProps<T> extends BoxProps {
  items: T[];
  itemWidth: number; // px
  gap: number; // px
  children: (item: T) => ReactNode;
  hideButtons?: boolean;
}

export const FixedWidthTileCarousel = <T,>(props: CarouselProps<T>) => {
  const isDesktop = useIsMinDesktopSize();

  const { items, itemWidth, gap, children, hideButtons, ...rest } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const tilesRef = useRef<(HTMLDivElement | null)[]>([]);

  const [scrollable, setScrollable] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pageItemsCount, setPageItemsCount] = useState(0);
  const [isPartiallyVisible, setIsPartiallyVisible] = useState(false);
  const [contentAreaWidth, setContentAreaWidth] = useState(0);

  useEffect(() => {
    const checkScrollable = () => {
      if (hideButtons) {
        return;
      }
      if (containerRef.current && items.length !== 0) {
        const { width: containerWidth } = containerRef.current.getBoundingClientRect();
        if (containerWidth === 0) {
          requestAnimationFrame(checkScrollable);
          return;
        }
        setContentAreaWidth(containerWidth);
        const totalWidth = items.length * itemWidth + (items.length - 1) * gap;
        setScrollable(totalWidth > containerWidth);

        const isPartiallyVisible = containerWidth % (itemWidth + gap) !== 0;
        setIsPartiallyVisible(isPartiallyVisible);

        const pageItemsCount = Math.ceil(containerWidth / (itemWidth + gap));
        setPageItemsCount(pageItemsCount);
      }
    };
    checkScrollable();
    window.addEventListener("resize", checkScrollable);

    return () => {
      window.removeEventListener("resize", checkScrollable);
    };
  }, [items.length, itemWidth, gap, hideButtons]);

  const scrollRight = () => {
    let nextIndex = currentIndex + pageItemsCount;
    nextIndex = isPartiallyVisible ? nextIndex - 1 : nextIndex;
    if (items.length <= nextIndex) {
      nextIndex = items.length - 1;
    }
    setCurrentIndex(nextIndex);
    scrollToIndex(nextIndex);
  };

  const scrollLeft = () => {
    let prevIndex = currentIndex - pageItemsCount;
    prevIndex = isPartiallyVisible ? prevIndex + 1 : prevIndex;
    if (prevIndex <= 0) {
      prevIndex = 0;
    }
    setCurrentIndex(prevIndex);
    scrollToIndex(prevIndex);
  };

  const scrollToIndex = (index: number) => {
    const element = tilesRef.current[index];
    if (element) {
      element.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
    }
  };

  const itemsRowWidth = pageItemsCount * itemWidth + (pageItemsCount - 1) * gap;

  const showButtons = scrollable && isDesktop && !hideButtons;

  const ContentComponent = isDesktop ? CarouselContent : SliderMobile;

  return (
    <Root {...rest}>
      <ContentComponent ref={containerRef} sx={{ gap: `${gap}px` }}>
        {items.map((item, index) => (
          <div
            key={index}
            ref={(el) => (tilesRef.current[index] = el)}
            style={index === items.length - 1 ? { paddingRight: contentAreaWidth - itemsRowWidth } : {}}
          >
            {children(item)}
          </div>
        ))}
      </ContentComponent>

      {showButtons && (
        <>
          {currentIndex !== 0 && (
            <NavigationButton
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                scrollLeft();
              }}
              sx={{
                left: "var(--arrow-offset, 0)",
              }}
            >
              <ArrowBackIosIcon sx={{ fontSize: 16 }} />
            </NavigationButton>
          )}
          {items.length - currentIndex >= pageItemsCount && (
            <NavigationButton
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                scrollRight();
              }}
              sx={{
                right: "var(--right-arrow-offset, 0)",
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
            </NavigationButton>
          )}
        </>
      )}
    </Root>
  );
};

const NavigationButton = styled(
  withDefaults(IconButton, {
    size: "large",
    variant: "contained",
    color: "secondary",
  }),
)({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
});

export const Root = styled(Box)({
  position: "relative",
  width: "100%",
});

export const CarouselContent = styled(Box)({
  display: "flex",
  width: "100%",
  overflowX: "hidden",
});

export const SliderMobile = styled(Box)({
  overflowX: "auto",
  paddingBottom: 12,
  display: "flex",
  scrollbarWidth: "none",
});
